/* eslint-disable @typescript-eslint/no-explicit-any */
import { isLocalhost } from '@app/app/lib';

const addApiPrefix = (path: string) => (isLocalhost ? `/api${path}` : path);

const createPrefixedFunction =
  (fn: (...args: any[]) => string) =>
  (...args: any[]) =>
    addApiPrefix(fn(...args));

const processServerPaths = (obj: any): any => {
  if (typeof obj === 'string') {
    return addApiPrefix(obj);
  }
  if (typeof obj === 'function') {
    return createPrefixedFunction(obj);
  }
  if (typeof obj === 'object' && obj !== null) {
    const result: any = {};
    for (const key in obj) {
      result[key] = processServerPaths(obj[key]);
    }
    return result;
  }
  return obj;
};

export const serverPath = processServerPaths({
  main: '/',
  process: '/process/',
  checkAuth: '/check-auth/',
  sessionPing: '/session-ping/',
  projects: '/projects/',
  project: '/project/',
  share: '/share/',
  shareInfo: '/share-info/',
  feed: '/feed/',
  saveSurveyData: '/save-survey-data/',
  staticISample3: '/static/i/sample.pptx?3',
  queuePing: '/queue-ping/',
  removeVisual: '/remove-visual/',
  setColor: '/set-color/',
  remake: '/remake/',
  download: '/download/',
  downloadPing: '/download-ping/',
  upload: '/upload/',
  uploadLogo: '/upload-logo/',
  deleteLogo: '/logo-delete/',
  searchMedia: '/search-media/',
  init: {
    common: '/init/common/',
    user: '/init/user/',
    theme: '/init/theme/',
    product: '/init/product/',
  },
  stripe: {
    stripe_subscription_cancel: '/stripe/subscription/cancel/',
  },
  team: {
    teamRequest: '/team/request/',
  },
  user: {
    logout: '/logout/',
    login: '/login/',
    passwordReset: '/password-reset/',
    register: '/register/',
    password: '/password/',
    updateOptions: '/update-options/',
    sendEmailConfirm: '/send-email-confirm/',
    purchase: '/purchase/',
  },
  draft: {
    draftCreate: '/create-draft/',
    draftUpdateParams: '/draft/update-params/',
    draftProcess: '/draft/process/',
    draftRemoveVisual: '/draft/remove-visual/',
    draftCreateV2: '/draft/create/',
    draftRemoveSlide: '/draft/remove-slide/',
    draftCloneSlide: '/draft/clone-slide/',
  },
  slideShooting: {
    slideShootingVersion: '/slide-shooting/versions/',
    slideShootingTheme: '/slide-shooting/themes/',
    slideShootingSetOptions: '/slide-shooting/set-option/',
    slideShootingProcess: '/slide-shooting/process/',
    slideShootingQueuePing: '/slide-shooting/queue-ping/',
    slideShootingFiles: '/slide-shooting/files/',
  },
  slide: {
    addSlide: '/add-slide/',
    cloneSlide: '/clone-slide/',
    remakeSlide: '/remake-slide/',
    processSlide: '/process-slide/',
    removeSlide: '/remove-slide/',
    regenSlide: '/regen-slide/',
    uploadImages: '/upload-images/',
    removeVisual: '/remove-visual/',
  },
  logo: {
    logoDelete: '/logo-delete/',
    logoUpload: '/upload-logo/',
  },
  preset: {
    deletePreset: '/delete-preset/',
  },
  theme: {
    initTheme: 'init/theme',
  },
  media: {
    uploadMedia: `/media/upload/`,
    listMedia: `/media/list/`,
    generateImage: `/media/generate/`,
    cancelImageGeneration: `/media/generate/cancel/`,
    generationStatus: (requestId: string) => `/media/generate/${requestId}/status/`,
  },
  // TODO: fix url workspace/id/slide/id/
  workspace: {
    uploadPPTX: '/workspace/upload/',
    createEmpty: `/workspace/create-empty/`,
    chatUpload: `/workspace/chat/upload/`,
    getUpdate: (workspaceId: string) => `/workspace/${workspaceId}/update/`,
    delete: (workspaceId: string) => `/workspace/${workspaceId}/update/`,
    downloadPresentationPrepare: (workspaceId: string) =>
      `/workspace/${workspaceId}/download/prepare/`,
    slidesDataSSE: (workspaceId: string) => `/workspace/${workspaceId}/slide/queue/`,
    downloadPresentationSSE: (workspaceId: string) => `/workspace/${workspaceId}/download/ready/`,
    downloadPresentationReady: (workspaceId: string) =>
      `/workspace/${workspaceId}/download/presentation.pptx`,
    initialSlidesData: (workspaceId: string) => `/workspace/${workspaceId}/generation-info/`,
    process: (workspaceId: string) => `/workspace/${workspaceId}/process/`,
    addSlide: (workspaceId: string) => `/workspace/${workspaceId}/slide/add/`,
    changePrompt: (workspaceId: string) => `/workspace/${workspaceId}/chat/change/`,
    deleteSlide: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/remove/`,
    copySlide: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/copy/`,
    moveSlide: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/move/`,
    saveSlideConfig: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/save-config/`,
    getSlideConfig: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/get-config/`,
    share: (workspaceId: string) => `/workspace/${workspaceId}/share/`,
    getMarkup: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/get-markup/`,
    editSlideMarkup: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/edit/`,
    getMissingItems: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/get-missing-items/`,
    processSlide: (workspaceId: string, slideId: number) =>
      `/workspace/${workspaceId}/slide/${slideId}/process/`,
    saveThumbnail: (workspaceId: string) => `/workspace/${workspaceId}/save-thumbnail/`,
    getLayouts: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/get-layouts/`,
    remakeLayout: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/remake/`,
    repaint: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/repaint/`,
    mediaBlocks: (workspaceId: string, slideId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/media-blocks/`,
    deleteMediaBlock: (workspaceId: string, slideId: string, blockId: string) =>
      `/workspace/${workspaceId}/slide/${slideId}/media-blocks/${blockId}/`,
    getStyleInfo: (workspaceId: string) => `/workspace/${workspaceId}/style-info/`,
  },
  // New Chat Widget endpoints (API v1)
  newChat: {
    // Auth
    me: '/api/v1/auth/me/',

    // Chats
    chats: '/api/v1/chats/',
    chatHistory: (chatId: string) => `/api/v1/chats/${chatId}/`,
    deleteChat: (chatId: string) => `/api/v1/chats/${chatId}/`,
    updateChat: (chatId: string) => `/api/v1/chats/${chatId}/`,
    editSlide: (chatId: string) => `/api/v1/chats/${chatId}/edit-slide/`,

    // Outline & Slides
    outline: (chatId: string) => `/api/v1/chats/${chatId}/outline/`,
    updateSlide: (chatId: string, slideId: string) =>
      `/api/v1/chats/${chatId}/outline/slides/${slideId}/`,
    createSlide: (chatId: string) => `/api/v1/chats/${chatId}/outline/slides/`,
    deleteSlide: (chatId: string, slideId: string) =>
      `/api/v1/chats/${chatId}/outline/slides/${slideId}/`,
    reorderSlides: (chatId: string) => `/api/v1/chats/${chatId}/outline/reorder/`,
    batchOperations: (chatId: string) => `/api/v1/chats/${chatId}/outline/batch/`,

    // Files
    uploadFile: '/api/v1/files/',
    fileMetadata: (fileId: string) => `/api/v1/files/${fileId}/`,
    deleteFile: (fileId: string) => `/api/v1/files/${fileId}/`,

    // Images
    generateImage: '/api/v1/images/generate/',

    // Presentation
    savePresentation: (chatId: string) => `/api/v1/chats/${chatId}/save-presentation/`,
    exportPresentation: (presentationId: string) =>
      `/api/v1/presentations/${presentationId}/export/`,

    // Health
    health: '/api/v1/health/',

    // SSE
    sseEvents: (userId: string) => `/sse/${userId}`,
  },
});
